import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { useAuth } from '../../../hooks/useAuth';
import { subscriptionsApi } from '../../../api/subscriptions';
import { isInTrial, trialDaysLeft } from '../../../types/subscription.types';

const DISMISS_KEY = 'webleads_trial_banner_dismissed';
const WARNING_THRESHOLD_DAYS = 2;
const REFRESH_INTERVAL_MS = 60000;

const TrialBanner: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [daysLeft, setDaysLeft] = useState<number | null>(null);
  const [chargeDate, setChargeDate] = useState<string | null>(null);
  const [priceDisplay, setPriceDisplay] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem(DISMISS_KEY) === 'true');

  useEffect(() => {
    if (!isAuthenticated) return;

    let cancelled = false;

    const load = async () => {
      try {
        const [{ subscription }, { plans }] = await Promise.all([
          subscriptionsApi.getCurrentSubscription(),
          subscriptionsApi.getPlans(),
        ]);
        if (cancelled) return;

        if (subscription && isInTrial(subscription)) {
          setDaysLeft(trialDaysLeft(subscription.current_period_end));
          setChargeDate(
            subscription.current_period_end
              ? new Date(subscription.current_period_end).toLocaleDateString('en-US', { month: 'long', day: 'numeric' })
              : null
          );
          setPriceDisplay(plans.find((p) => p.name === subscription.plan_name)?.price_display ?? null);
        } else {
          setDaysLeft(null);
        }
      } catch (error) {
        console.error('Failed to load trial banner data:', error);
      }
    };

    load();
    const interval = setInterval(load, REFRESH_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [isAuthenticated]);

  if (daysLeft === null) return null;

  const isWarning = daysLeft <= WARNING_THRESHOLD_DAYS;
  const dismissable = !isWarning;

  if (dismissable && dismissed) return null;

  const handleDismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, 'true');
    setDismissed(true);
  };

  const daysLabel = daysLeft === 0 ? 'Trial ends today' : `${daysLeft} ${daysLeft === 1 ? 'day' : 'days'} left`;

  return (
    <div className={isWarning ? 'border-y border-amber-300 bg-amber-50 py-3' : 'border-y border-accent/30 bg-accent/10 py-2.5'}>
      <div className="mx-auto flex max-w-7xl items-center gap-3 px-4 sm:px-6 lg:px-8">
        <span
          className={`flex flex-none items-center justify-center rounded-full ${isWarning ? 'bg-amber-500' : 'bg-accent'}`}
          style={{ width: 26, height: 26 }}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={2.4} className="h-3.5 w-3.5">
            {isWarning ? (
              <>
                <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </>
            ) : (
              <>
                <circle cx="12" cy="12" r="9" />
                <path d="M12 8h.01M11 12h1v4h1" />
              </>
            )}
          </svg>
        </span>

        <div className="flex flex-1 flex-wrap items-center gap-2 text-sm">
          <b className={`font-bold ${isWarning ? 'text-amber-800' : 'text-[#245C74]'}`}>
            {isWarning ? 'Trial ending' : 'Trial'}
          </b>
          <span className={`h-1 w-1 rounded-full ${isWarning ? 'bg-amber-400' : 'bg-accent/50'}`} />
          <b className={`font-bold ${isWarning ? 'text-amber-800' : 'text-[#245C74]'}`}>{daysLabel}</b>
          {priceDisplay && chargeDate && (
            <>
              <span className={`h-1 w-1 rounded-full ${isWarning ? 'bg-amber-400' : 'bg-accent/50'}`} />
              <span className={isWarning ? 'text-amber-700' : 'text-[#3E7189]'}>
                then{' '}
                <b className={`font-bold ${isWarning ? 'text-amber-800' : 'text-[#245C74]'}`}>{priceDisplay}/mo</b> on{' '}
                {chargeDate}
              </span>
            </>
          )}
        </div>

        <button
          onClick={() => navigate('/subscriptions')}
          className={`flex-none border-b-[1.5px] pb-px text-[13.5px] font-bold ${
            isWarning ? 'border-amber-500 text-amber-800' : 'border-accent/60 text-[#245C74]'
          }`}
        >
          Manage
        </button>

        {dismissable && (
          <button onClick={handleDismiss} title="Dismiss for this session" className="flex-none px-0.5 text-gray-500 opacity-60 hover:opacity-100">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
};

export default TrialBanner;
